(function () {
  'use strict';

  function normalizeColorKey(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  // Shared cache of every panel/texture image, keyed by URL, so every column
  // and category tab reuses the same load instead of re-fetching. Lives for
  // the page's lifetime (module-level, never persisted) — a refresh clears it,
  // matching the "never save the customer's choices" requirement.
  const imageCache = new Map(); // url -> { img, state: 'pending'|'ready'|'error', promise }

  function loadImage(url) {
    let record = imageCache.get(url);
    if (record) return record;

    const img = new Image();
    img.fetchPriority = 'low';
    record = { img, state: 'pending', promise: null };
    record.promise = new Promise((resolve) => {
      const settle = (state) => {
        record.state = state;
        resolve(record);
      };
      img.onload = () => settle('ready');
      // A failed URL is dropped from the cache so a later cycle can retry it
      // instead of permanently placeholdering a panel after one network blip.
      img.onerror = () => {
        imageCache.delete(url);
        settle('error');
      };
    });
    img.src = url;
    imageCache.set(url, record);
    return record;
  }

  class PanelConfiguratorCategory {
    constructor(root, data, host) {
      this.root = root;
      this.host = host;
      this.data = data;
      this.template = host.querySelector('[data-panel-configurator-column-template]');
      this.wall = root.querySelector('[data-panel-configurator-wall]');
      this.columnsInput = root.querySelector('[data-panel-configurator-columns] .quantity__input');
      this.summaryEl = root.querySelector('[data-panel-configurator-summary]');
      this.totalEl = root.querySelector('[data-panel-configurator-total]');
      this.textureList = root.querySelector('[data-panel-configurator-texture-list]');
      this.serviceToggle = root.querySelector('[data-panel-configurator-service-toggle]');

      this.selectedColor = (this.data.colors[0] && this.data.colors[0].key) || null;
      this.columns = this.createDefaultColumns();

      this.renderTextures();
      this.render();
      this.bindEvents();
      this.preloadAllImages();
    }

    // Fetches every panel image (every product × every color in this category)
    // into the browser's cache right away, so cycling with the arrows or
    // switching texture later is instant instead of showing the shimmer.
    preloadAllImages() {
      const urls = new Set();
      this.data.panels.forEach((panel) => {
        Object.values(panel.variantsByColor).forEach((entry) => {
          if (entry.image) urls.add(entry.image);
        });
      });
      urls.forEach(loadImage);
    }

    visiblePanelsForColor(colorKey) {
      return this.data.panels.filter((panel) => panel.variantsByColor[colorKey]);
    }

    resolveColumnPanel(column) {
      if (column.productId === null) return null;

      let panel = this.data.panels.find((p) => p.productId === column.productId && p.variantsByColor[this.selectedColor]);
      if (!panel) {
        const fallback = this.visiblePanelsForColor(this.selectedColor)[0];
        if (fallback) {
          column.productId = fallback.productId;
          panel = fallback;
        }
      }
      return panel || null;
    }

    createDefaultColumns() {
      const count = Math.max(this.data.columnsMin, Math.min(this.data.columnsStart, this.data.columnsMax));
      // Optional pre-fill: walk the category's products in block order and wrap
      // around (3 products, 4 columns -> 1, 2, 3, 1). Only products that have an
      // image for the active color are used, so no column starts blank.
      const available = this.data.autofill ? this.visiblePanelsForColor(this.selectedColor) : [];
      return Array.from({ length: count }, (_, index) => ({
        productId: available.length ? available[index % available.length].productId : null,
        rotated: false
      }));
    }

    renderTextures() {
      if (!this.textureList || !this.data.colors.length) return;

      const template = this.host.querySelector('[data-panel-configurator-texture-template]');
      if (!template) return;

      this.textureList.innerHTML = '';
      this.data.colors.forEach((color) => {
        const item = template.content.firstElementChild.cloneNode(true);
        const button = item.querySelector('[data-panel-configurator-texture-swatch]');
        const label = item.querySelector('[data-panel-configurator-texture-label]');

        // Swatch images share the same URLs as panel images (a color's
        // representative image is one of its panels' images) — routing
        // through the shared cache dedupes that fetch instead of loading it
        // a second time via a separate throwaway Image().
        const record = loadImage(color.image);
        if (record.state === 'ready') {
          button.style.setProperty('--swatch-background-image', `url(${color.image})`);
        }
        else {
          button.classList.add('is-loading');
          record.promise.then((resolved) => {
            button.classList.remove('is-loading');
            if (resolved.state === 'ready') {
              button.style.setProperty('--swatch-background-image', `url(${color.image})`);
            }
          });
        }

        button.setAttribute('data-color-key', color.key);
        button.setAttribute('aria-label', color.name);
        label.textContent = color.name;
        button.addEventListener('click', () => this.setColor(color.key));

        this.textureList.appendChild(item);
      });

      this.updateTextureSelection();
    }

    updateTextureSelection() {
      if (!this.textureList) return;
      this.textureList.querySelectorAll('[data-panel-configurator-texture-swatch]').forEach((button) => {
        const isActive = button.getAttribute('data-color-key') === this.selectedColor;
        button.classList.toggle('is-selected', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    setColor(colorKey) {
      if (colorKey === this.selectedColor) return;
      this.selectedColor = colorKey;
      this.updateTextureSelection();
      this.render();
    }

    render() {
      // Reconcile by index: reuse existing column elements, add/remove the rest.
      const existingEls = this.columnElements();

      this.columns.forEach((column, index) => {
        let el = existingEls[index];
        if (!el) {
          el = this.template.content.firstElementChild.cloneNode(true);
          this.wall.appendChild(el);
        }
        this.applyColumnContent(el, column);
      });

      while (this.wall.children.length > this.columns.length) {
        this.wall.lastElementChild.remove();
      }

      this.updateColumnsInput();
      this.updateSummary();
      this.updateTotal();
    }

    // Bare column: just the panel image, hidden while no product is assigned.
    applyColumnContent(el, column) {
      const panel = this.resolveColumnPanel(column);
      const entry = panel ? panel.variantsByColor[this.selectedColor] : null;
      const img = el.querySelector('[data-panel-configurator-img]');
      const placeholder = el.querySelector('[data-panel-configurator-placeholder]');

      // Small SKU caption under the panel + full name as a hover pill on the panel.
      const codeEl = el.querySelector('[data-panel-configurator-code]');
      const code = (entry && entry.sku) || '';
      codeEl.textContent = code;
      codeEl.title = code;
      el.querySelector('.panel-configurator__panel').setAttribute('data-title', panel ? panel.title : '');

      el.classList.toggle('is-rotated', column.rotated);
      el.querySelector('[data-panel-configurator-rotated-badge]').hidden = !column.rotated;
      const rotateButton = el.querySelector('[data-panel-configurator-rotate]');
      rotateButton.setAttribute('aria-pressed', String(column.rotated));
      rotateButton.classList.toggle('button--primary', column.rotated);
      rotateButton.classList.toggle('button--secondary', !column.rotated);

      // Match the real image proportions so the empty slot is exactly as tall as
      // a filled one: this column's own image, else the first available panel's
      // image in the active color, else the category's nominal width/height.
      const reference = entry || (this.visiblePanelsForColor(this.selectedColor)[0] || {}).variantsByColor?.[this.selectedColor];
      el.style.setProperty('--panel-aspect-ratio', String((reference && reference.aspectRatio) || this.data.aspectRatio));

      if (entry && entry.image) {
        img.alt = panel.title;
        img.src = entry.image;
        img.hidden = false;
        placeholder.hidden = true;
      }
      else {
        img.removeAttribute('src');
        img.hidden = true;
        placeholder.hidden = false;
      }
    }

    bindEvents() {
      this.wall.addEventListener('click', this.onClick.bind(this));
      this.root.querySelector('[data-panel-configurator-columns] [name="plus"]').addEventListener('click', () => this.addColumn());
      const randomizeButton = this.root.querySelector('[data-panel-configurator-randomize]');
      if (randomizeButton) randomizeButton.addEventListener('click', () => this.randomizeLayout(randomizeButton));
      this.root.querySelectorAll('[data-panel-configurator-add-side]').forEach((button) => {
        button.addEventListener('click', () => this.addColumn(button.getAttribute('data-panel-configurator-add-side')));
      });
      this.root.querySelector('[data-panel-configurator-columns] [name="minus"]').addEventListener('click', () => this.removeLastColumn());
      this.root.querySelector('[data-panel-configurator-add-to-cart]').addEventListener('click', () => this.addToCart());

      this.previewDialog = this.root.querySelector('[data-panel-configurator-preview]');
      this.root.querySelector('[data-panel-configurator-preview-open]').addEventListener('click', () => this.openPreview());
      this.root.querySelector('[data-panel-configurator-preview-close]').addEventListener('click', () => this.previewDialog.close());
      // Click on the dark backdrop (the dialog element itself, not its content) closes it.
      this.previewDialog.addEventListener('click', (event) => {
        if (event.target === this.previewDialog) this.previewDialog.close();
      });
      window.addEventListener('resize', () => {
        if (this.previewDialog.open) this.renderPreview();
      });
      this.root.querySelectorAll('[data-panel-configurator-download]').forEach((button) => {
        button.addEventListener('click', (event) => this.downloadImage(event.currentTarget));
      });

      if (this.serviceToggle) {
        this.serviceToggle.addEventListener('change', () => this.updateTotal());
      }
    }

    // Randomizes EVERY column, including empty ones added later, using only the
    // panels the customer has already picked on the wall: placed panels are reused,
    // empty columns get a random one of those same kinds, then the whole row is
    // shuffled and each panel flips at random (50/50). If nothing has been picked
    // yet, any panel of this category can be used.
    randomizeLayout(button) {
      const available = this.visiblePanelsForColor(this.selectedColor).map((panel) => panel.productId);
      const placed = this.columns.map((column) => column.productId).filter((productId) => productId !== null);
      const kinds = placed.length ? Array.from(new Set(placed)) : available;
      if (!kinds.length) return;

      const pick = () => kinds[Math.floor(Math.random() * kinds.length)];
      const productIds = this.columns.map((column) => (column.productId !== null ? column.productId : pick()));

      for (let i = productIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [productIds[i], productIds[j]] = [productIds[j], productIds[i]];
      }

      this.columns.forEach((column, index) => {
        column.productId = productIds[index];
        column.rotated = Math.random() < 0.5;
      });
      this.render();

      const dice = button && button.querySelector('.panel-configurator__dice');
      if (dice) {
        dice.classList.remove('is-rolling');
        void dice.getBoundingClientRect();
        dice.classList.add('is-rolling');
      }
    }

    openPreview() {
      this.renderPreview();
      this.previewDialog.showModal();
    }

    // Draws every column's panel edge to edge and picks the largest height at
    // which the whole row still fits the screen (sum of the panels' aspect ratios).
    renderPreview() {
      const stage = this.previewDialog.querySelector('[data-panel-configurator-preview-stage]');
      const items = this.columns.map((column) => {
        const panel = this.resolveColumnPanel(column);
        const entry = panel ? panel.variantsByColor[this.selectedColor] : null;
        return {
          image: entry && entry.image,
          title: panel ? panel.title : '',
          ratio: (entry && entry.aspectRatio) || this.data.aspectRatio,
          rotated: column.rotated
        };
      });

      // Vertical room is larger so the panels never sit under the top buttons.
      const availableWidth = window.innerWidth - 96;
      const availableHeight = window.innerHeight - 200;
      const totalRatio = items.reduce((sum, item) => sum + item.ratio, 0) || 1;
      const height = Math.floor(Math.min(availableHeight, availableWidth / totalRatio));

      stage.style.height = `${height}px`;
      stage.innerHTML = '';
      items.forEach((item) => {
        const el = document.createElement(item.image ? 'img' : 'div');
        el.className = 'panel-configurator__preview-panel';
        el.style.aspectRatio = String(item.ratio);
        if (item.image) {
          el.src = item.image;
          el.alt = item.title;
        }
        else {
          el.classList.add('is-empty');
        }
        el.classList.toggle('is-rotated', item.rotated);
        stage.appendChild(el);
      });
    }

    // Draws the arranged panels (same order, rotation and empty slots as the
    // preview) onto a canvas and saves it as a PNG. Images are fetched with CORS
    // so the canvas stays exportable.
    async downloadImage(button) {
      // Two download buttons exist (under the wall and inside the popup), each
      // with its own error message next to it.
      const errorEls = this.root.querySelectorAll('[data-panel-configurator-download-error]');
      errorEls.forEach((el) => { el.hidden = true; });
      button.disabled = true;
      button.classList.add('loading');

      try {
        const items = await Promise.all(this.columns.map(async (column) => {
          const panel = this.resolveColumnPanel(column);
          const entry = panel ? panel.variantsByColor[this.selectedColor] : null;
          let bitmap = null;
          if (entry && entry.image) {
            const response = await fetch(entry.image, { mode: 'cors' });
            if (!response.ok) throw new Error('image');
            bitmap = await createImageBitmap(await response.blob());
          }
          return {
            bitmap,
            rotated: column.rotated,
            title: panel ? panel.title : '',
            sku: (entry && entry.sku) || '',
            ratio: (bitmap && bitmap.width / bitmap.height) || (entry && entry.aspectRatio) || this.data.aspectRatio
          };
        }));

        const labels = this.previewDialog.dataset;
        const padding = 40;
        const numberRow = 56;
        const lineHeight = 40;
        const totalRatio = items.reduce((sum, item) => sum + item.ratio, 0) || 1;
        // Height from the tallest source image, capped so very long rows stay under canvas limits.
        const sourceHeight = Math.max(...items.map((item) => (item.bitmap ? item.bitmap.height : 0)), 1200);
        const height = Math.min(sourceHeight, Math.floor(12000 / totalRatio));
        const rowWidth = Math.round(totalRatio * height);

        const canvas = document.createElement('canvas');
        // Wide enough for the panel list even when only a few panels are placed.
        canvas.width = Math.max(rowWidth + padding * 2, 1400);
        canvas.height = padding + numberRow + height + padding + items.length * lineHeight + padding;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.textBaseline = 'middle';

        // Panels, numbered 1..n from left to right (rotation applied, empty slots grey).
        let x = Math.round((canvas.width - rowWidth) / 2);
        const top = padding + numberRow;
        items.forEach((item, index) => {
          const width = Math.round(item.ratio * height);
          if (item.bitmap) {
            ctx.save();
            ctx.translate(x + width / 2, top + height / 2);
            if (item.rotated) ctx.rotate(Math.PI);
            ctx.drawImage(item.bitmap, -width / 2, -height / 2, width, height);
            ctx.restore();
          }
          else {
            ctx.fillStyle = '#eeeeee';
            ctx.fillRect(x, top, width, height);
          }
          ctx.fillStyle = '#111111';
          ctx.font = '600 32px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(String(index + 1), x + width / 2, padding + numberRow / 2);
          x += width;
        });

        // List in the same order: "1. name (code) - rotated / not rotated".
        ctx.textAlign = 'left';
        ctx.font = '28px sans-serif';
        const listLeft = padding;
        items.forEach((item, index) => {
          const name = item.title || labels.labelEmpty;
          const code = item.sku && item.title ? ` (${item.sku})` : '';
          const state = item.title && item.rotated ? ` - ${labels.labelRotated}` : '';
          ctx.fillStyle = item.rotated ? '#b3261e' : '#111111';
          ctx.fillText(`${index + 1}. ${name}${code}${state}`, listLeft, top + height + padding + index * lineHeight + lineHeight / 2);
        });

        const blob = await new Promise((resolve, reject) => canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('blob'))), 'image/png'));
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'konfiguracja-paneli.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      }
      catch (error) {
        errorEls.forEach((el) => { el.hidden = false; });
      }
      finally {
        button.disabled = false;
        button.classList.remove('loading');
      }
    }

    onClick(event) {
      const columnEl = event.target.closest('[data-panel-configurator-column]');
      if (!columnEl) return;
      const index = this.columnElements().indexOf(columnEl);

      if (event.target.closest('[data-panel-configurator-remove]')) {
        this.removeColumn(index);
        return;
      }

      if (event.target.closest('[data-panel-configurator-rotate]')) {
        this.rotateColumn(index);
        return;
      }

      const button = event.target.closest('[data-panel-configurator-cycle]');
      if (!button) return;
      this.cycleProduct(index, button.getAttribute('data-panel-configurator-cycle') === 'up' ? 1 : -1);
    }

    // Flips the panel 180°: the state lives on the column, applyColumnContent()
    // reflects it (upside-down image, "180°" badge, pressed rotate button).
    rotateColumn(index) {
      const column = this.columns[index];
      if (!column) return;
      column.rotated = !column.rotated;
      this.applyColumnContent(this.columnElements()[index], column);
    }

    removeColumn(index) {
      if (this.columns.length <= this.data.columnsMin) return;
      this.columns.splice(index, 1);
      this.render();
    }

    // Switches this column to the next (up) / previous (down) product that has
    // an image in the active color; an empty column starts from the first one.
    cycleProduct(index, delta) {
      const column = this.columns[index];
      if (!column) return;

      const visible = this.visiblePanelsForColor(this.selectedColor);
      if (!visible.length) return;

      if (column.productId === null) {
        column.productId = visible[0].productId;
      }
      else {
        const currentPos = Math.max(0, visible.findIndex((p) => p.productId === column.productId));
        column.productId = visible[(currentPos + delta + visible.length) % visible.length].productId;
      }

      this.applyColumnContent(this.columnElements()[index], column);
      this.updateSummary();
      this.updateTotal();
    }

    columnElements() {
      return Array.from(this.wall.querySelectorAll('[data-panel-configurator-column]'));
    }

    // `side` 'start' inserts a new empty column before the first panel; anything
    // else (the quantity control's plus) appends it after the last one.
    addColumn(side) {
      if (this.columns.length >= this.data.columnsMax) return;
      const column = { productId: null, rotated: false };
      if (side === 'start') this.columns.unshift(column);
      else this.columns.push(column);
      this.render();

      // When the row overflows (small screen or many panels), bring the new panel
      // into view. block: 'nearest' keeps the page itself from jumping vertically.
      const columns = this.columnElements();
      const added = side === 'start' ? columns[0] : columns[columns.length - 1];
      if (added) added.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    removeLastColumn() {
      if (this.columns.length <= this.data.columnsMin) return;
      this.columns.pop();
      this.render();
    }

    updateColumnsInput() {
      if (this.columnsInput) {
        this.columnsInput.value = this.columns.length;
      }
      // Delete buttons are disabled once the category is at its minimum.
      const atMin = this.columns.length <= this.data.columnsMin;
      this.wall.querySelectorAll('[data-panel-configurator-remove]').forEach((button) => {
        button.disabled = atMin;
      });
      const atMax = this.columns.length >= this.data.columnsMax;
      this.root.querySelectorAll('[data-panel-configurator-add-side]').forEach((button) => {
        button.disabled = atMax;
      });
    }

    updateSummary() {
      const counts = new Map();
      this.columns.forEach((column) => {
        const panel = this.resolveColumnPanel(column);
        if (!panel) return;
        counts.set(panel.title, (counts.get(panel.title) || 0) + 1);
      });

      this.summaryEl.innerHTML = '';
      counts.forEach((count, title) => {
        const row = document.createElement('div');
        row.className = 'panel-configurator__summary-row text-sm';

        const titleEl = document.createElement('span');
        titleEl.className = 'panel-configurator__summary-title';
        titleEl.textContent = title;
        titleEl.title = title;

        const countEl = document.createElement('span');
        countEl.className = 'panel-configurator__summary-count';
        countEl.textContent = `× ${count}`;

        row.append(titleEl, countEl);
        this.summaryEl.appendChild(row);
      });
    }

    updateTotal() {
      const subtotal = this.columns.reduce((sum, column) => {
        const panel = this.resolveColumnPanel(column);
        const entry = panel ? panel.variantsByColor[this.selectedColor] : null;
        return sum + (entry ? entry.price : 0);
      }, 0);

      const servicePrice = (this.serviceToggle && this.serviceToggle.checked)
        ? parseInt(this.serviceToggle.getAttribute('data-price'), 10) || 0
        : 0;

      this.totalEl.innerHTML = window.theme.Currency.formatMoney(subtotal + servicePrice, window.theme.settings.moneyFormat);
    }

    addToCart() {
      const items = new Map();
      this.columns.forEach((column) => {
        const panel = this.resolveColumnPanel(column);
        const entry = panel ? panel.variantsByColor[this.selectedColor] : null;
        if (!entry) return;
        items.set(entry.variantId, (items.get(entry.variantId) || 0) + 1);
      });

      if (!items.size) return;

      if (this.serviceToggle && this.serviceToggle.checked) {
        const serviceVariantId = parseInt(this.serviceToggle.getAttribute('data-variant-id'), 10);
        if (serviceVariantId) {
          items.set(serviceVariantId, (items.get(serviceVariantId) || 0) + 1);
        }
      }

      const submitButton = this.root.querySelector('[data-panel-configurator-add-to-cart]');
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.setAttribute('aria-busy', 'true');

      const body = JSON.stringify({
        items: Array.from(items.entries()).map(([id, quantity]) => ({ id, quantity }))
      });

      fetch(window.theme.routes.cart_add_url, { ...window.theme.utils.fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            return;
          }

          const cartJson = await (await fetch(window.theme.routes.cart_url, { ...window.theme.utils.fetchConfig('json', 'GET') })).json();
          cartJson['sections'] = parsedState['sections'];

          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'panel-configurator', cart: cartJson });
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('aria-busy');
        });
    }
  }

  class PanelConfigurator extends HTMLElement {
    constructor() {
      super();
      this.categories = [];
    }

    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');

      const validCategoryIds = new Set();
      this.querySelectorAll('[data-panel-configurator-category]').forEach((root) => {
        const data = this.readCategoryData(root);
        if (!data.panels.length) {
          root.remove();
          return;
        }
        validCategoryIds.add(data.id);
        this.categories.push(new PanelConfiguratorCategory(root, data, this));
      });

      const tabsContainer = this.querySelector('[data-panel-configurator-tabs]');
      this.querySelectorAll('[data-panel-configurator-tab]').forEach((tab) => {
        const categoryId = tab.getAttribute('data-category-id');
        if (!validCategoryIds.has(categoryId)) {
          tab.remove();
          return;
        }
        tabsContainer.appendChild(tab);
        tab.addEventListener('click', () => this.selectCategory(categoryId));
      });

      const firstCategory = this.querySelector('[data-panel-configurator-category]');
      if (firstCategory) {
        this.selectCategory(firstCategory.getAttribute('data-category-id'));
      }
    }

    readCategoryData(root) {
      const colorsByKey = new Map();
      const panels = [];

      root.querySelectorAll('[data-panel-configurator-product]').forEach((productEl) => {
        const dataScript = productEl.querySelector('[data-panel-product-data]');
        if (!dataScript) return;

        const product = JSON.parse(dataScript.textContent);
        const variantImages = Array.from(productEl.querySelectorAll('[data-panel-variant-image]')).map((script) => JSON.parse(script.textContent));

        const variantsByColor = {};
        variantImages.forEach(({ colorName, image, aspectRatio }) => {
          const variant = product.variants.find((v) => normalizeColorKey(v.colorValue) === normalizeColorKey(colorName));
          if (!variant) return;

          const key = normalizeColorKey(variant.colorValue);
          variantsByColor[key] = { variantId: variant.variantId, price: variant.price, image, aspectRatio, sku: variant.sku };

          if (!colorsByKey.has(key)) {
            colorsByKey.set(key, { key, name: variant.colorValue, image });
          }
        });

        if (Object.keys(variantsByColor).length) {
          panels.push({ productId: product.productId, title: product.title, variantsByColor });
        }
      });

      return {
        id: root.getAttribute('data-category-id'),
        template: root.getAttribute('data-template'),
        aspectRatio: parseFloat(root.getAttribute('data-aspect-ratio')),
        columnsStart: parseInt(root.getAttribute('data-columns-start'), 10),
        columnsMin: parseInt(root.getAttribute('data-columns-min'), 10),
        columnsMax: parseInt(root.getAttribute('data-columns-max'), 10),
        autofill: root.getAttribute('data-autofill') === 'true',
        colors: Array.from(colorsByKey.values()),
        panels
      };
    }

    selectCategory(categoryId) {
      this.querySelectorAll('[data-panel-configurator-tab]').forEach((tab) => {
        const isActive = tab.getAttribute('data-category-id') === categoryId;
        tab.classList.toggle('button--primary', isActive);
        tab.classList.toggle('button--secondary', !isActive);
        tab.toggleAttribute('disabled', isActive);
      });

      this.querySelectorAll('[data-panel-configurator-category]').forEach((panel) => {
        panel.toggleAttribute('hidden', panel.getAttribute('data-category-id') !== categoryId);
      });
    }
  }

  customElements.define('panel-configurator', PanelConfigurator);
})();
